<template>
  <div class="cell-controls">
    <template v-if="showName">
      <div class="row-name" :title="rowName">{{ rowName }}</div>
    </template>
    <template v-else>
      <v-select
        :value="selectedOption"
        :items="dropdownOptions"
        item-text="label"
        item-value="value"
        class="store-group-dropdown"
        :class="{ 'dropdown-expanded': isExpanded }"
        dense
        hide-details
        :disabled="!hasDropdownOptions"
        :loading="splitLoading && isExpanded"
        :append-icon="dropdownAppendIcon"
        @change="onDropdownChange"
      />
    </template>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { get } from 'lodash';

export default {
  name: 'ReportingControlsCellRenderer',

  computed: {
    ...mapState('reporting', [
      'expandedRow',
      'selectedHierarchyRow',
      'splitLoading',
      'selectedAttributes',
    ]),

    showName() {
      return (
        get(this.params, 'data.isStoreGroupRow', false) ||
        get(this.params, 'data.isAttributeRow', false)
      );
    },

    rowName() {
      if (!this.showName) return '';
      const data = get(this.params, 'data');
      if (data.isAttributeRow) {
        return data.attributeValue || '';
      }
      return data.name || data.toolStoreGroupKey || '';
    },

    dropdownOptions() {
      const options = [
        {
          value: 'storeGroups',
          label: this.$t('reporting.config.storeGroups'),
        },
      ];

      if (this.selectedAttributes && this.selectedAttributes.length > 0) {
        this.selectedAttributes.forEach(attr => {
          options.push({
            value: attr.key,
            label: attr.displayDescription || attr.key,
          });
        });
      }

      return options;
    },

    hasDropdownOptions() {
      return this.dropdownOptions.length > 0;
    },

    dropdownAppendIcon() {
      if (!this.hasDropdownOptions) return '';
      return this.isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down';
    },

    isExpanded() {
      const { data } = this.params;
      if (!data || !this.expandedRow) return false;

      return this.expandedRow.levelEntryKey === data.levelEntryKey;
    },

    selectedOption() {
      const { data } = this.params;
      if (!data) return null;

      // If this row is expanded, return the expanded option
      if (this.isExpanded && this.expandedRow) {
        return this.expandedRow.selectedOption;
      }

      // Otherwise check if there's a pending selection for this row
      if (!this.selectedHierarchyRow) return null;

      // Return the selected option only if it matches this row's levelEntryKey
      if (this.selectedHierarchyRow.levelEntryKey === data.levelEntryKey) {
        return this.selectedHierarchyRow.selectedOption;
      }
      return null;
    },
  },

  methods: {
    ...mapActions('reporting', ['toggleRowExpansion', 'selectHierarchyRow']),

    onDropdownChange(value) {
      const { data, node } = this.params;
      if (!data) return;

      // Expand the tree node
      if (node) {
        node.setExpanded(true);
      }

      // Trigger the row expansion with split data fetch
      this.toggleRowExpansion({
        levelEntryKey: data.levelEntryKey,
        level: data.level,
        groupBy: value,
        selectedOption: value,
        workpackageIdForNode: data.workpackageIdForNode,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.cell-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.store-group-dropdown {
  width: 130px;

  ::v-deep .v-input__slot {
    background-color: transparent !important;
  }

  ::v-deep .v-select__selection {
    font-size: 1.1rem;
  }

  ::v-deep .v-input__append-inner {
    margin-top: 4px;
  }
}

.row-name {
  font-weight: 600;
  font-size: 1.4rem;
  line-height: 1.7rem;
  padding: 0 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
